import type { JSX } from "react";
import { logData } from "@jb-dotcom/fe-logger";
import { AsappClient } from "./client";

type AsappState = Partial<{
  asCustomerId: boolean;
  isActiveSession: boolean;
  isFullscreen: boolean;
  isLiveChat: boolean;
  isMobile: boolean;
  isOpen: boolean;
  unreadMessages: number;
}>;

type AsappProperties = {
  asappAppId?: string | undefined;
  asappHostname?: string | undefined;
  affiliateId?: string | undefined;
  sherpaAppId?: string | undefined;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- window must be an interface
  interface Window {
    ASAPP?: {
      (action: "getState", callback: (state: AsappState) => void): void;
      (action: "load", config: typeof defaultLoadProperties): void;
      (action: string, ...args: unknown[]): void;
    };
  }
}

const defaultLoadProperties = {
  APIHostname: "jetblue.asapp.com",
  AppId: "jetblue",
  Display: {
    Align: "right",
    AlwaysShowMinimize: true,
    BadgeColor: "#0033a0",
    BadgeType: "badge",
    DarkColor: "#121212",
    PrimaryColor: "#0033a0",
  },
  Language: "en-US",
};

const handleAsappLoad = (asappAppId: string, asappHostname: string) => {
  console.log("ASAPP: loaded");

  // eslint-disable-next-line unicorn/prefer-global-this -- window.ASAPP
  if (window.ASAPP) {
    // eslint-disable-next-line unicorn/prefer-global-this -- window.ASAPP
    window.ASAPP("getState", (state: AsappState) => {
      if (state.isActiveSession) {
        const loadProperties = {
          ...defaultLoadProperties,
          APIHostname: asappHostname,
          AppId: asappAppId,
        };
        void logData({
          loadProperties: loadProperties,
          message: "ASAPP: Active session detected, loading chat",
        });
        // eslint-disable-next-line unicorn/prefer-global-this -- window.ASAPP
        if (typeof window.ASAPP === "function") {
          // eslint-disable-next-line unicorn/prefer-global-this -- window.ASAPP
          window.ASAPP("load", defaultLoadProperties);
        }
      } else {
        void logData(
          "ASAPP: No active session detected, chat will not be loaded",
        );
      }
    });
  }
};

export function Asapp({
  asappAppId,
  asappHostname,
  affiliateId,
  sherpaAppId,
}: Readonly<AsappProperties>): JSX.Element | null {
  if (!asappAppId || !asappHostname || !affiliateId || !sherpaAppId) {
    return null;
  }
  return (
    <AsappClient
      affiliateId={affiliateId}
      asappAppId={asappAppId}
      asappHostname={asappHostname}
      sherpaAppId={sherpaAppId}
    />
  );
}

Asapp.asappLoad = handleAsappLoad;

export default Asapp;
